/**
 * SearchableSelect.tsx
 * A Select whose dropdown carries a search box, for picking one column out of
 * a wide sheet without scrolling through dozens of headers.
 *
 * Matching ignores case, diacritics and separators (the same normalisation the
 * field matcher uses), so "so po" finds "Số PO".
 */
import * as React from "react";
import { Search } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { normalizeHeader } from "@/utils/columnMatcher";
import { cn } from "@/lib/utils";

interface Props {
  value: string;
  onValueChange: (value: string) => void;
  options: string[];
  /** Always-visible first entry, e.g. `-- Không --`. */
  emptyOption?: { value: string; label: string };
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchPlaceholder?: string;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  emptyOption,
  placeholder,
  disabled,
  className,
  searchPlaceholder = "Tìm tên cột…",
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const filtered = React.useMemo(() => {
    const q = normalizeHeader(query);
    if (!q) return options;
    // The selected column stays listed even when filtered out, otherwise the
    // trigger loses its label while the user is typing.
    return options.filter((o) => normalizeHeader(o).includes(q) || o === value);
  }, [options, query, value]);

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <SelectTrigger className={cn("h-9 text-xs", className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <div className="sticky top-0 z-10 -mx-1 -mt-1 mb-1 border-b bg-popover px-2 py-1.5">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 w-full rounded-md border bg-background pl-7 pr-2 text-xs outline-none focus:ring-1 focus:ring-ring"
              // Radix Select owns keyboard typeahead; without this, typing
              // would jump between options instead of reaching the box.
              onKeyDown={(e) => {
                if (!["Escape", "Tab", "Enter", "ArrowDown", "ArrowUp"].includes(e.key)) {
                  e.stopPropagation();
                }
              }}
              onPointerDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        {emptyOption && (
          <SelectItem value={emptyOption.value}>{emptyOption.label}</SelectItem>
        )}
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-center text-[11px] text-muted-foreground">
            Không có cột nào khớp “{query}”.
          </p>
        ) : (
          filtered.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}
